import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { PageProcess } from '../models';

interface PageProcessResponse {
  id: number;
  pageId: number;
  pageName: string;
  status: number;
  result?: any;
  mes: number;
  año: number;
  processType: number;
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageProcessService {
  private apiUrl = '/api/pageprocesses';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAllProcesses(): Observable<PageProcess[]> {
    return this.http.get<PageProcessResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToPageProcess(r)))
    );
  }

  getProcessesByPage(pageId: number): Observable<PageProcess[]> {
    return this.http.get<PageProcessResponse[]>(`${this.apiUrl}/page/${pageId}`, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToPageProcess(r)))
    );
  }

  getProcessById(id: number): Observable<PageProcess> {
    return this.http.get<PageProcessResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToPageProcess(response))
    );
  }

  getProcessesByMonth(mes: number, año: number): Observable<PageProcess[]> {
    return this.http.get<PageProcessResponse[]>(`${this.apiUrl}/month/${mes}/${año}`, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToPageProcess(r)))
    );
  }

  deleteProcess(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  executeAllAudits(): Observable<any> {
    return this.http.post(`${this.apiUrl}/execute-all`, {}, {
      headers: this.getHeaders()
    });
  }

  createMonthlyAudits(month?: number, year?: number): Observable<any> {
    const params = new HttpParams();
    if (month !== undefined) params.set('month', month);
    if (year !== undefined) params.set('year', year);

    return this.http.post(`${this.apiUrl}/create-monthly-audits`, {}, {
      headers: this.getHeaders(),
      params: params as any
    });
  }

  getAuditReportBlob(processId: number): Observable<Blob> {
    return this.http.get(`/api/audits/${processId}/report`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
  }

  sendAuditReport(processId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/${processId}/send-report`, {}, {
      headers: this.getHeaders()
    });
  }

  private mapToPageProcess(response: PageProcessResponse): PageProcess {
    return {
      id: response.id,
      pageId: response.pageId,
      pageName: response.pageName,
      status: response.status,
      result: response.result,
      mes: response.mes,
      anio: response.año,
      processType: response.processType,
      createdAt: new Date(response.createdAt),
      updatedAt: new Date(response.updatedAt)
    };
  }
}
